import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import config from "../config";
import plusIcon from "../public/img/plus.svg";
import arrow from "../public/img/arrow.svg";
import historyIconDark from "../public/img/history_icon_dark.svg";
import "./ImageGen.css";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { Markdown } from "tiptap-markdown";

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const SEEDREAM_SIZE_OPTIONS = ["1k", "2k", "4k"];
// Sizes for the older OpenAI image models (gpt-image-1, gpt-image-1.5, etc.)
const OPENAI_LEGACY_SIZE_OPTIONS = [
  { value: "1024x1024", label: "1024x1024 (Square)" },
  { value: "1536x1024", label: "1536x1024 (Landscape)" },
  { value: "1024x1536", label: "1024x1536 (Portrait)" },
];
// gpt-image-2 currently only accepts the same fixed sizes as gpt-image-1, plus auto.
const OPENAI_GPT_IMAGE_2_SIZE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "1024x1024", label: "1024x1024 (Square)" },
  { value: "1536x1024", label: "1536x1024 (Landscape)" },
  { value: "1024x1536", label: "1024x1536 (Portrait)" },
];
const GROK_ASPECT_RATIO_OPTIONS = [
  "1:1", "16:9", "9:16", "4:3", "3:4", "3:2", "2:3",
  "2:1", "1:2", "19.5:9", "9:19.5", "20:9", "9:20", "auto"
];
const GROK_RESOLUTION_OPTIONS = ["1k", "2k"];
const FLUX_SIZE_OPTIONS = ["1024x1024", "1536x1024", "1024x1536", "1344x768", "768x1344"];
const FLUX_ASPECT_RATIO_OPTIONS = ["1:1", "16:9", "9:16", "4:3", "3:4", "3:2", "2:3"];
const FLUX_OUTPUT_FORMAT_OPTIONS = ["jpeg", "png"];
const FLUX_MODEL_IDS = new Set([
  "flux-2-max",
  "flux-2-pro",
  "flux-2-flex",
  "flux-2-klein-4b",
  "flux-2-klein-9b",
  "flux-kontext-max",
  "flux-kontext-pro",
  "flux-pro-1.1-ultra",
  "flux-pro-1.1",
  "flux-pro",
]);
const FLUX_ASPECT_RATIO_MODEL_IDS = new Set([
  "flux-kontext-max",
  "flux-kontext-pro",
  "flux-pro-1.1-ultra",
]);
const FLUX_SIZE_MODEL_IDS = new Set([
  "flux-2-max",
  "flux-2-pro",
  "flux-2-flex",
  "flux-2-klein-4b",
  "flux-2-klein-9b",
  "flux-pro-1.1",
  "flux-pro",
]);

const isOpenAiImageModel = (modelId) => modelId?.startsWith("openai-");
const isGptImage2Model = (modelId) => modelId === "openai-gpt-image-2";
const getOpenAiSizeOptions = (modelId) =>
  isGptImage2Model(modelId) ? OPENAI_GPT_IMAGE_2_SIZE_OPTIONS : OPENAI_LEGACY_SIZE_OPTIONS;
const isSeeDreamModel = (modelId) => modelId === "atlas-seedream-4.5";
const isGrokModel = (modelId) =>
  modelId === "grok-imagine-image" || modelId === "grok-imagine-image-pro";
const isFluxModel = (modelId) => FLUX_MODEL_IDS.has(modelId);
const isFluxSizeModel = (modelId) => FLUX_SIZE_MODEL_IDS.has(modelId);
const isFluxAspectModel = (modelId) => FLUX_ASPECT_RATIO_MODEL_IDS.has(modelId);

const getDefaultSlotConfig = (modelId = "openai-gpt-image-1.5") => ({
  model: modelId,
  size: isSeeDreamModel(modelId)
    ? "2k"
    : (isFluxSizeModel(modelId) ? "1024x1024" : "1024x1024"),
  quality: "medium",
  aspectRatio: isGrokModel(modelId)
    ? "auto"
    : (isFluxAspectModel(modelId) ? "1:1" : "1:1"),
  resolution: isGrokModel(modelId) ? "1k" : "1K",
  outputFormat: isFluxModel(modelId) ? "jpeg" : undefined,
  seed: "",
  safetyTolerance: 2,
  promptUpsampling: false,
  steps: 50,
  guidance: 4.5,
  raw: false,
  imagePromptStrength: 0.1,
});

const normalizeSeeDreamSize = (sizeValue) => {
  if (!sizeValue) return "2k";
  const raw = String(sizeValue).trim().toLowerCase();

  const legacyMap = {
    "1024": "1k",
    "1536": "2k",
    "2048": "2k",
    "3072": "4k",
    "4096": "4k",
  };
  if (legacyMap[raw]) return legacyMap[raw];

  if (SEEDREAM_SIZE_OPTIONS.includes(raw)) return raw;

  // Keep WIDTHxHEIGHT values as-is if a history record already uses them.
  if (raw.includes("x")) {
    const [w, h] = raw.split("x");
    if (Number(w) > 0 && Number(h) > 0) return `${Number(w)}x${Number(h)}`;
  }

  return "2k";
};

const ImageGen = () => {
  const navigate = useNavigate();
  const { user, isCheckingAuth } = useAuth();

  const [messages, setMessages] = useState([]);
  const [selectedModel, setSelectedModel] = useState("openai-gpt-image-1.5");
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const wsInstance = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const fileInputRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  // Chat history sidebar state
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyCards, setHistoryCards] = useState([]);
  const [loadingHistoryCards, setLoadingHistoryCards] = useState(false);
  // Setter is used to track pagination state on the server response, but the
  // value isn't currently read in render — array hole keeps the lint happy.
  const [, setHasMoreHistory] = useState(false);
  const [currentHistoryId, setCurrentHistoryId] = useState(null);
  const currentHistoryIdRef = useRef(null);
  const historyListRef = useRef(null);
  const [historyError, setHistoryError] = useState(null);

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    cardId: null,
  });
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");

  // Multi-generation slots state
  const [generationSlots, setGenerationSlots] = useState([
    {
      id: 1,
      ...getDefaultSlotConfig("openai-gpt-image-1.5"),
    }
  ]);
  const generationSlotsRef = useRef(generationSlots);
  const activeRequestSlotIdsRef = useRef([]);
  const [slotSelectionState, setSlotSelectionState] = useState({ 1: true });
  const [activeSlotDropdown, setActiveSlotDropdown] = useState(null);
  const [activeResolutionDropdown, setActiveResolutionDropdown] = useState(null);
  const [activeAspectRatioDropdown, setActiveAspectRatioDropdown] = useState(null);
  const [activeResolutionValueDropdown, setActiveResolutionValueDropdown] = useState(null);
  const [activeQualityDropdown, setActiveQualityDropdown] = useState(null);
  const [activeOutputFormatDropdown, setActiveOutputFormatDropdown] = useState(null);

  // Keep ref in sync with state
  useEffect(() => {
    generationSlotsRef.current = generationSlots;
  }, [generationSlots]);

  // Ensure every visible slot has a checkbox state; removed slots are cleaned up
  useEffect(() => {
    setSlotSelectionState((prev) => {
      const next = {};
      generationSlots.forEach((slot) => {
        next[slot.id] = prev[slot.id] ?? true;
      });
      return next;
    });
  }, [generationSlots]);

  // Generated images state - tracks images for each slot
  const [generatedImages, setGeneratedImages] = useState({});
  const [generatingSlots, setGeneratingSlots] = useState(new Set());
  // Force-rerender bump — the value itself is never read; only the setter
  // call signals "re-render now" after a non-state mutation.
  const [, setRenderTrigger] = useState(0);

  // Lightbox state for viewing full images
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: true,
        orderedList: true,
      }),
      Underline,
      Placeholder.configure({
        placeholder: "Describe the image you want to generate...",
      }),
      Markdown.configure({
        html: false,
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "chat-input tiptap-editor",
      },
      handleKeyDown(view, event) {
        // Cmd/Ctrl+Enter -> send
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          event.preventDefault();
          handleSendFromEditor();
          return true;
        }

        // Enter (no modifiers) -> send, except in lists
        if (event.key === "Enter" && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
          if (
            editor?.isActive?.("bulletList") ||
            editor?.isActive?.("orderedList")
          ) {
            return false;
          }
          if (!isConnected) return true;
          event.preventDefault();
          handleSendFromEditor();
          return true;
        }

        return false;
      },
    },
  });

  const [editorHasContent, setEditorHasContent] = useState(false);

  useEffect(() => {
    if (!editor) return;
    const update = () => {
      try {
        const txt = editor.getText ? editor.getText().trim() : "";
        setEditorHasContent(txt.length > 0);
      } catch (e) {
        setEditorHasContent(false);
      }
    };
    editor.on("update", update);
    editor.on("selectionUpdate", update);
    update();
    return () => {
      try {
        editor.off("update", update);
        editor.off("selectionUpdate", update);
      } catch (e) {}
    };
  }, [editor]);

  const getEditorMarkdown = () => {
    if (!editor) return "";
    try {
      const md = editor?.storage?.markdown?.getMarkdown
        ? editor.storage.markdown.getMarkdown()
        : null;
      if (md != null) return md;
      return editor.getText();
    } catch (e) {
      return editor.getText();
    }
  };

  const clearEditor = () => {
    if (editor) editor.commands.clearContent(true);
  };

  const scrollToBottom = useCallback((instant = false) => {
    if (instant) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Effect for cleaning up object URLs. The cleanup intentionally runs only
  // on unmount and reads whatever attachmentPreviews exists at that moment;
  // adding it as a dep would revoke URLs that are still in use.
  useEffect(() => {
    return () => {
      attachmentPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll on new messages
  useLayoutEffect(() => {
    if (messages.length > 0) {
      scrollToBottom(false);
    }
  }, [messages, scrollToBottom]);

  // Fetch available models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(`${config.api.baseUrl}/image-gen/models`, {
          credentials: "include",
        });
        const data = await response.json();
        if (data.success) {
          setAvailableModels(data.models);
        }
      } catch (err) {
        console.error("Error fetching models:", err);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchModels();
  }, []);

  // Initialize WebSocket
  useEffect(() => {
    if (!user || isCheckingAuth) return;

    setIsConnecting(true);
    setIsConnected(false);

    const wsUrl = `${config.api.baseUrl.replace(/^http/, "ws")}/image-gen/ws`;
    console.log("[FRONTEND] Connecting to WebSocket:", wsUrl);
    const ws = new WebSocket(wsUrl);
    wsInstance.current = ws;

    ws.onopen = () => {
      console.log("[FRONTEND] WebSocket connected");
      setIsConnecting(false);
      setIsConnected(true);
      setChatError(null);

      // Send init message (token is read from cookies on the backend)
      const initPayload = {
        type: "init",
      };

      if (currentHistoryIdRef.current) {
        initPayload.history_id = currentHistoryIdRef.current;
      }

      console.log("[FRONTEND] Sending init message:", initPayload);
      ws.send(JSON.stringify(initPayload));
    };

    ws.onmessage = (event) => {
      console.log("[FRONTEND] WebSocket message received:", event.data.substring(0, 200));

      let serverMessage;
      try {
        serverMessage = JSON.parse(event.data);
        console.log("[FRONTEND] Parsed message type:", serverMessage.type);
      } catch (e) {
        console.error("[FRONTEND] Failed to parse message:", e);
        setChatError("Received an invalid message from the server.");
        return;
      }

      switch (serverMessage.type) {
        case "init_success":
          setChatError(null);
          if (serverMessage.model) {
            setSelectedModel(serverMessage.model);
          }
          break;

        case "history_created":
          setCurrentHistoryId(serverMessage.history_id);
          currentHistoryIdRef.current = serverMessage.history_id;
          // Add new history card
          const newCard = {
            _id: serverMessage.history_id,
            title: "New Image Generation",
            updated_at: new Date().toISOString(),
            created_at: new Date().toISOString(),
            model: selectedModel,
          };
          setHistoryCards((prev) => [newCard, ...prev]);
          break;

        case "user_message":
          setMessages((prev) => [...prev, serverMessage.message]);
          break;

        case "image_generated":
          console.log("[FRONTEND] Received image_generated message:", {
            slotIndex: serverMessage.slot_index,
            imageUrl: serverMessage.message?.image_url,
            mimeType: serverMessage.message?.mime_type,
            allComplete: serverMessage.all_complete
          });

          // Track generated image by slot
          const imageMessage = serverMessage.message;
          const slotIndex = serverMessage.slot_index;

          // Map backend slot_index to original slot IDs used in the latest request
          const currentSlots = generationSlotsRef.current;
          const requestSlotIds = activeRequestSlotIdsRef.current || [];
          console.log("[FRONTEND] Current generationSlots:", currentSlots);
          console.log("[FRONTEND] Looking for slot at index:", slotIndex);

          // Use flushSync to force immediate synchronous updates
          flushSync(() => {
            setMessages((prev) => [...prev, serverMessage.message]);

            if (
              typeof slotIndex !== 'undefined' &&
              requestSlotIds.length > 0 &&
              typeof requestSlotIds[slotIndex] !== "undefined"
            ) {
              const slotId = requestSlotIds[slotIndex];
              console.log("[FRONTEND] Found slot ID:", slotId, "Image URL:", imageMessage.image_url);

              setGeneratedImages(prev => {
                const updated = {
                  ...prev,
                  [slotId]: {
                    imageUrl: imageMessage.image_url,  // Store URL instead of base64
                    mimeType: imageMessage.mime_type,
                    model: imageMessage.model,
                    timestamp: imageMessage.timestamp
                  }
                };
                console.log("[FRONTEND] Updated generatedImages:", Object.keys(updated));
                return updated;
              });

              // Resolve true output dimensions and let card width follow the actual ratio.
              setTimeout(() => {
                captureGeneratedImageDimensions(slotId, {
                  imageUrl: imageMessage.image_url,
                  isBase64: false,
                });
              }, 0);

              setGeneratingSlots(prev => {
                const newSet = new Set(prev);
                newSet.delete(slotId);
                console.log("[FRONTEND] Removed from generatingSlots, remaining:", Array.from(newSet));
                return newSet;
              });

              // Force re-render by incrementing trigger
              setRenderTrigger(prev => prev + 1);
            } else {
              console.error("[FRONTEND] Could not find slot!", {
                slotIndex,
                requestSlotIds,
                slotsLength: currentSlots.length
              });
            }

            // Check if all images are done
            if (serverMessage.all_complete) {
              console.log("[FRONTEND] All images complete");
              activeRequestSlotIdsRef.current = [];
              setIsGenerating(false);
            }
          });
          break;

        case "error": {
          const slotIndex = serverMessage.slot_index;
          const requestSlotIds = activeRequestSlotIdsRef.current || [];

          if (
            typeof slotIndex !== "undefined" &&
            typeof requestSlotIds[slotIndex] !== "undefined"
          ) {
            const failedSlotId = requestSlotIds[slotIndex];
            setGeneratingSlots((prev) => {
              const next = new Set(prev);
              next.delete(failedSlotId);
              return next;
            });
          }

          if (serverMessage.all_complete) {
            activeRequestSlotIdsRef.current = [];
            setIsGenerating(false);
          }

          setChatError(serverMessage.message);
          break;
        }

        default:
          break;
      }
    };

    ws.onerror = (error) => {
      console.error("[FRONTEND] WebSocket error:", error);
      setChatError("Connection error occurred");
      setIsConnecting(false);
    };

    ws.onclose = (event) => {
      console.log("[FRONTEND] WebSocket closed:", event.code, event.reason);
      setIsConnected(false);
      setIsConnecting(false);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isCheckingAuth, selectedModel]);

  // Fetch history - removed from useCallback to prevent infinite loop
  const fetchHistory = async () => {
    if (loadingHistoryCards || !user) return;

    setLoadingHistoryCards(true);
    try {
      const skip = historyCards.length;
      const response = await fetch(
        `${config.api.baseUrl}/image-gen/history?skip=${skip}&limit=20`,
        { credentials: "include" }
      );
      const data = await response.json();

      if (data.success) {
        setHistoryCards((prev) => [...prev, ...data.history]);
        setHasMoreHistory(data.has_more);
      }
    } catch (err) {
      console.error("Error fetching history:", err);
      setHistoryError("Failed to load history");
    } finally {
      setLoadingHistoryCards(false);
    }
  };

  // Only fetch history once when sidebar opens
  useEffect(() => {
    if (isHistoryOpen && historyCards.length === 0 && !loadingHistoryCards) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoryOpen]);

  const loadImageDimensions = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = src;
    });

  const captureGeneratedImageDimensions = async (slotId, imageData) => {
    if (!slotId || !imageData?.imageUrl) return;
    const imageSrc = imageData.isBase64
      ? imageData.imageUrl
      : `${config.api.baseUrl}${imageData.imageUrl}`;
    const dimensions = await loadImageDimensions(imageSrc);
    if (!dimensions?.width || !dimensions?.height) return;

    setGeneratedImages((prev) => {
      const existing = prev[slotId];
      if (!existing) return prev;
      if (existing.width === dimensions.width && existing.height === dimensions.height) {
        return prev;
      }
      return {
        ...prev,
        [slotId]: {
          ...existing,
          width: dimensions.width,
          height: dimensions.height,
        },
      };
    });
  };

  const inferOpenAiSizeFromDimensions = (width, height, modelId) => {
    if (!width || !height) return "1024x1024";
    const ratio = width / height;
    const sizeList = getOpenAiSizeOptions(modelId)
      .filter((opt) => opt.value !== "auto")
      .map((opt) => {
        const [w, h] = opt.value.split("x").map(Number);
        return { value: opt.value, ratio: w / h, maxEdge: Math.max(w, h) };
      });
    const targetMaxEdge = Math.max(width, height);
    return sizeList.reduce((best, current) => {
      const ratioDiff = Math.abs(current.ratio - ratio) - Math.abs(best.ratio - ratio);
      if (Math.abs(ratioDiff) > 0.01) return ratioDiff < 0 ? current : best;
      // Ratios are close — prefer the option whose max edge is nearest the actual image.
      return Math.abs(current.maxEdge - targetMaxEdge) < Math.abs(best.maxEdge - targetMaxEdge)
        ? current
        : best;
    }).value;
  };

  const inferAspectRatioFromDimensions = (width, height) => {
    if (!width || !height) return "1:1";
    const ratio = width / height;
    const options = ["1:1", "2:3", "3:2", "3:4", "4:3", "4:5", "5:4", "9:16", "16:9", "21:9", "1:4", "4:1", "1:8", "8:1"];
    const toNumber = (value) => {
      const [w, h] = value.split(":").map(Number);
      return w / h;
    };
    return options.reduce((best, current) =>
      Math.abs(toNumber(current) - ratio) < Math.abs(toNumber(best) - ratio) ? current : best
    );
  };

  const inferResolutionFromDimensions = (width, height) => {
    const maxEdge = Math.max(width || 0, height || 0);
    if (maxEdge >= 3000) return "4K";
    if (maxEdge >= 1800) return "2K";
    return "1K";
  };

  const inferGrokResolutionFromDimensions = (width, height) => {
    const maxEdge = Math.max(width || 0, height || 0);
    return maxEdge >= 1800 ? "2k" : "1k";
  };

  const inferSeeDreamSizeFromDimensions = (width, height) => {
    const maxEdge = Math.max(width || 0, height || 0);
    if (maxEdge >= 3000) return "4k";
    if (maxEdge >= 1500) return "2k";
    return "1k";
  };

  const getSlotParamsLabel = (slot) => {
    if (isOpenAiImageModel(slot.model)) {
      return `${slot.size} • ${slot.quality}`;
    }
    if (isSeeDreamModel(slot.model)) {
      return `${slot.size}`;
    }
    if (isGrokModel(slot.model)) {
      return `${slot.aspectRatio} • ${slot.resolution}`;
    }
    if (isFluxModel(slot.model)) {
      const base = isFluxAspectModel(slot.model) ? slot.aspectRatio : slot.size;
      return `${base} • ${slot.outputFormat || "jpeg"}`;
    }
    return `${slot.aspectRatio} • ${slot.resolution}`;
  };

  // Load specific history
  const handleSelectHistoryCard = async (historyId) => {
    try {
      const response = await fetch(
        `${config.api.baseUrl}/image-gen/history/${historyId}`,
        { credentials: "include" }
      );
      const data = await response.json();

      if (data.success) {
        activeRequestSlotIdsRef.current = [];
        setGeneratingSlots(new Set());
        setIsGenerating(false);
        setMessages(data.messages);
        setCurrentHistoryId(historyId);
        currentHistoryIdRef.current = historyId;
        if (data.model) {
          setSelectedModel(data.model);
        }

        // Extract image messages from history
        const imageMessages = data.messages.filter(
          msg => msg.role === "assistant" && (msg.image_url || msg.image)
        );

        console.log("[HISTORY] Found image messages:", imageMessages.length);

        // Create slots for each image and populate generatedImages
        if (imageMessages.length > 0) {
          const newSlots = [];
          const newGeneratedImages = {};
          const usedSlotIds = new Set();

          for (let index = 0; index < imageMessages.length; index += 1) {
            const msg = imageMessages[index];
            const preferredSlotId = Number(msg.slot_id);
            let slotId = Number.isFinite(preferredSlotId) && preferredSlotId > 0
              ? preferredSlotId
              : index + 1;
            while (usedSlotIds.has(slotId)) {
              slotId += 1;
            }
            usedSlotIds.add(slotId);
            const model = msg.model || "openai-gpt-image-1.5";
            let savedSize = msg.size || null;
            const savedQuality = msg.quality || "medium";
            let savedAspectRatio = msg.aspectRatio || msg.aspect_ratio || null;
            let savedResolution = msg.resolution || null;
            const imageSrc = msg.image_url
              ? `${config.api.baseUrl}${msg.image_url}`
              : (msg.image ? `data:${msg.mime_type || "image/png"};base64,${msg.image}` : null);
            const imageDimensions = imageSrc ? await loadImageDimensions(imageSrc) : null;

            // Fallback for older histories that don't have slot params saved
            if (!savedSize || !savedAspectRatio || !savedResolution) {
              if (imageDimensions) {
                  if (isOpenAiImageModel(model)) {
                    savedSize = savedSize || inferOpenAiSizeFromDimensions(imageDimensions.width, imageDimensions.height, model);
                    savedAspectRatio = savedAspectRatio || inferAspectRatioFromDimensions(imageDimensions.width, imageDimensions.height);
                    savedResolution = savedResolution || "1K";
                  } else if (isSeeDreamModel(model)) {
                    savedSize = savedSize || inferSeeDreamSizeFromDimensions(imageDimensions.width, imageDimensions.height);
                    savedAspectRatio = savedAspectRatio || "1:1";
                    savedResolution = savedResolution || "1K";
                  } else if (isGrokModel(model)) {
                    savedAspectRatio = savedAspectRatio || inferAspectRatioFromDimensions(imageDimensions.width, imageDimensions.height);
                    savedResolution = savedResolution || inferGrokResolutionFromDimensions(imageDimensions.width, imageDimensions.height);
                    savedSize = savedSize || "1024x1024";
                  } else if (isFluxModel(model)) {
                    savedAspectRatio = savedAspectRatio || inferAspectRatioFromDimensions(imageDimensions.width, imageDimensions.height);
                    savedSize = savedSize || inferOpenAiSizeFromDimensions(imageDimensions.width, imageDimensions.height);
                    savedResolution = savedResolution || "1K";
                  } else {
                    savedAspectRatio = savedAspectRatio || inferAspectRatioFromDimensions(imageDimensions.width, imageDimensions.height);
                    savedResolution = savedResolution || inferResolutionFromDimensions(imageDimensions.width, imageDimensions.height);
                    savedSize = savedSize || "1024x1024";
                  }
              }
            }

            savedSize = savedSize || (isSeeDreamModel(model) ? "2k" : "1024x1024");
            if (isSeeDreamModel(model)) {
              savedSize = normalizeSeeDreamSize(savedSize);
            }
            savedAspectRatio = savedAspectRatio || "1:1";
            savedResolution = savedResolution || (isGrokModel(model) ? "1k" : "1K");

            // Create slot configuration
            newSlots.push({
              id: slotId,
              ...getDefaultSlotConfig(model),
              size: savedSize,
              quality: savedQuality,
              aspectRatio: savedAspectRatio,
              resolution: savedResolution,
              outputFormat: msg.output_format || "jpeg",
              seed: msg.seed ?? "",
              safetyTolerance: msg.safety_tolerance ?? 2,
              promptUpsampling: msg.prompt_upsampling ?? false,
              steps: msg.steps ?? 50,
              guidance: msg.guidance ?? 4.5,
              raw: msg.raw ?? false,
              imagePromptStrength: msg.image_prompt_strength ?? 0.1,
            });

            // Populate generated image data
            if (msg.image_url) {
              // Image saved as file (new format)
              newGeneratedImages[slotId] = {
                imageUrl: msg.image_url,
                mimeType: msg.mime_type || "image/png",
                model: model,
                timestamp: msg.timestamp,
                ...(imageDimensions ? {
                  width: imageDimensions.width,
                  height: imageDimensions.height
                } : {})
              };
            } else if (msg.image) {
              // Legacy base64 format - convert to data URL for display
              newGeneratedImages[slotId] = {
                imageUrl: `data:${msg.mime_type || "image/png"};base64,${msg.image}`,
                mimeType: msg.mime_type || "image/png",
                model: model,
                timestamp: msg.timestamp,
                isBase64: true, // Flag to indicate this is a base64 data URL
                ...(imageDimensions ? {
                  width: imageDimensions.width,
                  height: imageDimensions.height
                } : {})
              };
            }
          }

          console.log("[HISTORY] Setting up slots:", newSlots.length);
          console.log("[HISTORY] Generated images:", Object.keys(newGeneratedImages));

          setGenerationSlots(newSlots);
          setSlotSelectionState(
            newSlots.reduce((acc, slot) => {
              acc[slot.id] = true;
              return acc;
            }, {})
          );
          setGeneratedImages(newGeneratedImages);
        } else {
          // No images, reset to default single slot
          setGenerationSlots([
            {
              id: 1,
              ...getDefaultSlotConfig(data.model || "openai-gpt-image-1.5"),
            }
          ]);
          setSlotSelectionState({ 1: true });
          setGeneratedImages({});
        }
      }
    } catch (err) {
      console.error("Error loading history:", err);
      setChatError("Failed to load history");
    }
  };

  // Update history title
  const handleSaveTitle = async (cardId) => {
    const newTitle = editingTitleValue.trim();
    if (!newTitle) {
      setEditingTitleId(null);
      return;
    }

    try {
      const response = await fetch(
        `${config.api.baseUrl}/image-gen/history/${cardId}/title`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ title: newTitle }),
        }
      );
      const data = await response.json();

      if (data.success) {
        setHistoryCards((prev) =>
          prev.map((card) =>
            card._id === cardId ? { ...card, title: newTitle } : card
          )
        );
      }
    } catch (err) {
      console.error("Error updating title:", err);
    }
    setEditingTitleId(null);
  };

  // Delete history
  const handleDeleteHistory = async (cardId) => {
    if (!window.confirm("Are you sure you want to delete this history?")) {
      return;
    }

    try {
      const response = await fetch(
        `${config.api.baseUrl}/image-gen/history/${cardId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const data = await response.json();

      if (data.success) {
        setHistoryCards((prev) => prev.filter((card) => card._id !== cardId));
        if (currentHistoryIdRef.current === cardId) {
          setCurrentHistoryId(null);
          currentHistoryIdRef.current = null;
          setMessages([]);
        }
      }
    } catch (err) {
      console.error("Error deleting history:", err);
    }
  };

  const handleToggleHistory = () => {
    setIsHistoryOpen(!isHistoryOpen);
  };

  // Handle file input
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      setChatError("Please select image files only");
      return;
    }

    // Limit to 5 images
    if (attachedFiles.length + imageFiles.length > 5) {
      setChatError("Maximum 5 images allowed");
      return;
    }

    setAttachedFiles((prevFiles) => [...prevFiles, ...imageFiles]);

    const newPreviews = imageFiles.map((file) => ({
      file: file,
      url: URL.createObjectURL(file),
    }));
    setAttachmentPreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);
  };

  const handleRemoveFile = (index) => {
    URL.revokeObjectURL(attachmentPreviews[index].url);
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDraggingFile(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);

    const historyElements = e.target.closest('.history-sidebar-wrapper');
    if (historyElements) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter(f => f.type.startsWith("image/"));

      if (imageFiles.length === 0) {
        setChatError("Please drop image files only");
        return;
      }

      if (attachedFiles.length + imageFiles.length > 5) {
        setChatError("Maximum 5 images allowed");
        return;
      }

      setAttachedFiles((prevFiles) => [...prevFiles, ...imageFiles]);

      const newPreviews = imageFiles.map((file) => ({
        file: file,
        url: URL.createObjectURL(file),
      }));
      setAttachmentPreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);
    }
  };

  // Send message
  const handleSendFromEditor = async () => {
    console.log("[FRONTEND] handleSendFromEditor called", {
      isConnected,
      isGenerating,
      wsReadyState: wsInstance.current?.readyState,
      wsReadyStateNames: {
        0: "CONNECTING",
        1: "OPEN",
        2: "CLOSING",
        3: "CLOSED"
      }[wsInstance.current?.readyState]
    });

    if (!isConnected || isGenerating) {
      console.warn("[FRONTEND] Cannot send: not connected or already generating");
      return;
    }

    if (!wsInstance.current || wsInstance.current.readyState !== WebSocket.OPEN) {
      console.error("[FRONTEND] WebSocket is not open!", wsInstance.current?.readyState);
      setChatError("WebSocket connection is not ready. Please refresh the page.");
      return;
    }

    const prompt = getEditorMarkdown().trim();
    if (!prompt && attachedFiles.length === 0) {
      console.warn("[FRONTEND] No prompt or files to send");
      return;
    }

    const selectedSlots = generationSlots.filter(
      (slot) => slotSelectionState[slot.id] !== false
    );

    if (selectedSlots.length === 0) {
      setChatError("Select at least one image card to continue the prompt.");
      return;
    }

    setIsGenerating(true);
    setChatError(null);

    // Mark only selected slots as generating
    const selectedSlotIds = selectedSlots.map((slot) => slot.id);
    activeRequestSlotIdsRef.current = selectedSlotIds;
    setGeneratingSlots(new Set(selectedSlotIds));

    try {
      // Convert files to base64
      const filesData = await Promise.all(
        attachedFiles.map(async (file) => {
          const reader = new FileReader();
          return new Promise((resolve) => {
            reader.onload = () => {
              resolve({
                name: file.name,
                type: file.type,
                data: reader.result,
              });
            };
            reader.readAsDataURL(file);
          });
        })
      );

      // Group slots with their configurations
      const slotConfigs = selectedSlots.map(slot => ({
        slotId: slot.id,
        model: slot.model,
        size: slot.size,
        quality: slot.quality,
        aspectRatio: slot.aspectRatio,
        resolution: slot.resolution,
        outputFormat: slot.outputFormat,
        seed: slot.seed,
        safetyTolerance: slot.safetyTolerance,
        promptUpsampling: slot.promptUpsampling,
        steps: slot.steps,
        guidance: slot.guidance,
        raw: slot.raw,
        imagePromptStrength: slot.imagePromptStrength,
      }));

      // Send batch generation request
      const requestPayload = {
        type: "generate_batch",
        prompt: prompt,
        slots: slotConfigs, // Array of slot configurations with model and resolution
        files: filesData,
      };

      console.log("[FRONTEND] Sending generate_batch request:", {
        type: requestPayload.type,
        promptLength: prompt.length,
        slotsCount: slotConfigs.length,
        filesCount: filesData.length,
        selectedSlotIds,
        slots: slotConfigs
      });

      wsInstance.current.send(JSON.stringify(requestPayload));

      // Clear input
      clearEditor();
      setAttachedFiles([]);
      attachmentPreviews.forEach((p) => URL.revokeObjectURL(p.url));
      setAttachmentPreviews([]);
    } catch (err) {
      console.error("Error sending message:", err);
      setChatError("Failed to send message");
      activeRequestSlotIdsRef.current = [];
      setIsGenerating(false);
    }
  };

  // Context menu handlers
  const handleCardContextMenu = (e, cardId) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      cardId: cardId,
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, cardId: null });
  };

  useEffect(() => {
    if (contextMenu.visible) {
      document.addEventListener("click", closeContextMenu);
      return () => document.removeEventListener("click", closeContextMenu);
    }
  }, [contextMenu.visible]);

  // Close slot dropdown when clicking outside
  useEffect(() => {
    if (activeSlotDropdown !== null) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.slot-model-wrapper')) {
          setActiveSlotDropdown(null);
        }
      };
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeSlotDropdown]);

  // Close resolution dropdown when clicking outside
  useEffect(() => {
    if (activeResolutionDropdown !== null) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.slot-resolution-section')) {
          setActiveResolutionDropdown(null);
        }
      };
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeResolutionDropdown]);

  // Close aspect ratio dropdown when clicking outside
  useEffect(() => {
    if (activeAspectRatioDropdown !== null) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.slot-aspect-ratio-wrapper')) {
          setActiveAspectRatioDropdown(null);
        }
      };
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeAspectRatioDropdown]);

  // Close resolution value dropdown when clicking outside
  useEffect(() => {
    if (activeResolutionValueDropdown !== null) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.slot-resolution-value-wrapper')) {
          setActiveResolutionValueDropdown(null);
        }
      };
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeResolutionValueDropdown]);

  // Close quality dropdown when clicking outside
  useEffect(() => {
    if (activeQualityDropdown !== null) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.slot-quality-wrapper')) {
          setActiveQualityDropdown(null);
        }
      };
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeQualityDropdown]);

  // Close output format dropdown when clicking outside
  useEffect(() => {
    if (activeOutputFormatDropdown !== null) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.slot-output-format-wrapper')) {
          setActiveOutputFormatDropdown(null);
        }
      };
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeOutputFormatDropdown]);

  // Close lightbox on escape key
  useEffect(() => {
    if (lightboxOpen) {
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          closeLightbox();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [lightboxOpen]);

  // Multi-generation slot handlers
  const addGenerationSlot = () => {
    if (generationSlots.length < 8) {
      const newId = Math.max(...generationSlots.map(s => s.id), 0) + 1;
      const defaultModel = selectedModel || "openai-gpt-image-1.5";
      setGenerationSlots([
        ...generationSlots,
        {
          id: newId,
          ...getDefaultSlotConfig(defaultModel),
        }
      ]);
    }
  };

  const removeGenerationSlot = (slotId) => {
    if (generationSlots.length > 1) {
      setGenerationSlots(generationSlots.filter(s => s.id !== slotId));
    }
  };

  const toggleSlotSelection = (slotId) => {
    setSlotSelectionState((prev) => ({
      ...prev,
      [slotId]: !(prev[slotId] ?? true),
    }));
  };

  const updateSlotModel = (slotId, newModel) => {
    setGenerationSlots(generationSlots.map(s =>
      s.id === slotId ? {
        ...s,
        ...getDefaultSlotConfig(newModel),
      } : s
    ));
    setActiveSlotDropdown(null);
  };

  const updateSlotSize = (slotId, newSize) => {
    setGenerationSlots(generationSlots.map(s =>
      s.id === slotId ? { ...s, size: newSize } : s
    ));
    setActiveResolutionDropdown(null);
  };

  const updateSlotQuality = (slotId, newQuality) => {
    setGenerationSlots(generationSlots.map(s =>
      s.id === slotId ? { ...s, quality: newQuality } : s
    ));
    setActiveQualityDropdown(null);
  };

  const updateSlotAspectRatio = (slotId, newAspectRatio) => {
    setGenerationSlots(generationSlots.map(s =>
      s.id === slotId ? { ...s, aspectRatio: newAspectRatio } : s
    ));
    setActiveAspectRatioDropdown(null);
  };

  const updateSlotResolution = (slotId, newResolution) => {
    setGenerationSlots(generationSlots.map(s =>
      s.id === slotId ? { ...s, resolution: newResolution } : s
    ));
    setActiveResolutionValueDropdown(null);
  };

  const updateSlotOutputFormat = (slotId, newOutputFormat) => {
    setGenerationSlots(generationSlots.map(s =>
      s.id === slotId ? { ...s, outputFormat: newOutputFormat } : s
    ));
    setActiveOutputFormatDropdown(null);
  };

  // Lightbox handlers
  const openLightbox = (imageData, slot) => {
    setLightboxImage({
      ...imageData,
      modelName: availableModels.find(m => m.id === slot.model)?.name,
      params: getSlotParamsLabel(slot)
    });
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  const downloadImage = (imageData, filename) => {
    const link = document.createElement('a');
    // Handle both base64 data URLs and file URLs
    link.href = imageData.isBase64 ? imageData.imageUrl : `${config.api.baseUrl}${imageData.imageUrl}`;
    link.download = filename || `generated-image-${Date.now()}.png`;
    link.target = '_blank';  // Open in new tab as fallback
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGeneratedImageLoad = (slotId, e) => {
    const width = e?.target?.naturalWidth;
    const height = e?.target?.naturalHeight;
    if (!width || !height) return;

    setGeneratedImages((prev) => {
      const existing = prev[slotId];
      if (!existing) return prev;
      if (existing.width === width && existing.height === height) return prev;
      return {
        ...prev,
        [slotId]: {
          ...existing,
          width,
          height,
        },
      };
    });
  };

  const handleEditTitle = (cardId, currentTitle) => {
    setEditingTitleId(cardId);
    setEditingTitleValue(currentTitle);
    closeContextMenu();
  };

  const handleCancelEditTitle = () => {
    setEditingTitleId(null);
  };

  const getSlotAspectRatio = (slot, generated) => {
    if (!slot) return 1;

    // Prefer actual rendered image dimensions when available.
    if (generated?.width && generated?.height && generated.height > 0) {
      return generated.width / generated.height;
    }

    // OpenAI sizes are in WxH format, e.g. 1536x1024
    if (isOpenAiImageModel(slot.model)) {
      const size = slot.size || "1024x1024";
      const [w, h] = size.split("x").map(Number);
      if (Number.isFinite(w) && Number.isFinite(h) && h > 0) {
        return w / h;
      }
      return 1;
    }

    // SeeDream size is a single edge value; render as square.
    if (isSeeDreamModel(slot.model)) {
      return 1;
    }

    if (isFluxModel(slot.model)) {
      if (isFluxSizeModel(slot.model)) {
        const [w, h] = String(slot.size || "1024x1024").split("x").map(Number);
        if (Number.isFinite(w) && Number.isFinite(h) && h > 0) {
          return w / h;
        }
        return 1;
      }
      const [w, h] = String(slot.aspectRatio || "1:1").split(":").map(Number);
      if (Number.isFinite(w) && Number.isFinite(h) && h > 0) {
        return w / h;
      }
      return 1;
    }

    // Gemini ratios are in W:H format, e.g. 16:9
    const ratio = slot.aspectRatio || "1:1";
    const [w, h] = ratio.split(":").map(Number);
    if (Number.isFinite(w) && Number.isFinite(h) && h > 0) {
      return w / h;
    }
    return 1;
  };

  if (isCheckingAuth) {
    return (
      <Layout>
        <div className="chat-page">
          <div className="chat-container">
            <div className="loading">Loading...</div>
          </div>
        </div>
      </Layout>
    );
  }

  const hasSelectedSlots = generationSlots.some(
    (slot) => slotSelectionState[slot.id] !== false
  );

  return (
    <Layout>
      <div
        className="chat-page"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="chat-container">
          <div
            className={`chat-history-page-container ${
              isHistoryOpen ? "history-open" : "history-closed"
            }`}
            onClick={(e) => {
              if (
                isHistoryOpen &&
                window.innerWidth <= 1150 &&
                !e.target.closest(".history-sidebar-wrapper")
              ) {
                setIsHistoryOpen(false);
              }
            }}
          >
            {/* History Sidebar */}
            <div
              className="history-sidebar-wrapper"
              onClick={(e) => {
                if (!isHistoryOpen && !e.target.closest('button')) {
                  handleToggleHistory();
                }
              }}
              style={{ cursor: !isHistoryOpen ? 'pointer' : 'default' }}
            >
              <div className="history-sidebar-panel">
                <div className="history-header">
                  <button
                    className="new-chat-btn"
                    title="Start new image generation"
                    onClick={() => {
                      activeRequestSlotIdsRef.current = [];
                      setGeneratingSlots(new Set());
                      setIsGenerating(false);
                      setMessages([]);
                      setCurrentHistoryId(null);
                      currentHistoryIdRef.current = null;
                      setChatError(null);
                      clearEditor();
                      setAttachedFiles([]);
                      attachmentPreviews.forEach((p) =>
                        URL.revokeObjectURL(p.url)
                      );
                      setAttachmentPreviews([]);
                      // Reset slots and generated images
                      setGenerationSlots([
                        {
                          id: 1,
                          ...getDefaultSlotConfig(selectedModel || "openai-gpt-image-1.5"),
                        }
                      ]);
                      setSlotSelectionState({ 1: true });
                      setGeneratedImages({});
                      navigate(`/image-gen`, { replace: true });
                      if (window.innerWidth <= 630) {
                        setIsHistoryOpen(false);
                      }
                    }}
                  >
                    <img src={plusIcon} alt="New" className="new-chat-icon" />
                  </button>
                  <button
                    onClick={handleToggleHistory}
                    className="history-sidebar-toggle-btn"
                    title={isHistoryOpen ? "Close History" : "Open History"}
                  >
                    <img
                      src={historyIconDark}
                      alt="History"
                      className="history-toggle-icon"
                    />
                  </button>
                </div>
                {loadingHistoryCards && (
                  <div className="history-loading">Loading…</div>
                )}
                {historyError && (
                  <div className="history-error">{historyError}</div>
                )}
                {!loadingHistoryCards &&
                  !historyError &&
                  historyCards.length === 0 && (
                    <div className="history-empty">
                      Image generations will be saved here
                    </div>
                  )}
                <div className="history-list" ref={historyListRef}>
                  {historyCards.map((card) => (
                    <div
                      key={card._id}
                      className={`history-card ${
                        currentHistoryId === card._id ? "active" : ""
                      }`}
                      onContextMenu={(e) => handleCardContextMenu(e, card._id)}
                      onClick={(e) => {
                        if (editingTitleId !== card._id) {
                          handleSelectHistoryCard(card._id);
                          if (window.innerWidth <= 630) {
                            setIsHistoryOpen(false);
                          }
                        }
                      }}
                    >
                      <div className="history-card-content">
                        {editingTitleId === card._id ? (
                          <input
                            type="text"
                            className="history-card-title-input"
                            value={editingTitleValue}
                            onChange={(e) => setEditingTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSaveTitle(card._id);
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                handleCancelEditTitle();
                              }
                            }}
                            onBlur={() => handleSaveTitle(card._id)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div className="history-card-title">
                            {card.title || "Untitled"}
                          </div>
                        )}
                        <div className="history-card-sub">
                          {card.model || "Unknown model"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {contextMenu.visible && (
                <div
                  className="history-context-menu"
                  style={{
                    position: "fixed",
                    left: contextMenu.x,
                    top: contextMenu.y,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="menu-item"
                    onClick={() => {
                      const card = historyCards.find(
                        (c) => c._id === contextMenu.cardId
                      );
                      if (card) {
                        handleEditTitle(contextMenu.cardId, card.title);
                      }
                    }}
                  >
                    Edit Title
                  </div>
                  <div
                    className="menu-item delete"
                    onClick={() => {
                      handleDeleteHistory(contextMenu.cardId);
                      closeContextMenu();
                    }}
                  >
                    Delete
                  </div>
                </div>
              )}
            </div>

            {/* Main Image Gen Area */}
            <div className="image-gen-panel">
              {isDraggingFile && (
                <div className="file-drop-overlay">
                  <div className="file-drop-content">
                    <svg
                      width="56"
                      height="56"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p className="file-drop-text">Drop images here to attach</p>
                  </div>
                </div>
              )}

              {/* Connection status messages */}
              {isConnecting && (
                <div className="info-message connecting">
                  Connecting to image generation service...
                </div>
              )}
              {!isConnected && !isConnecting && !chatError && (
                <div className="info-message disconnected">
                  Not connected. Attempting to reconnect...
                </div>
              )}

              {/* Main Generation Slots Area - Full width content area */}
              <div className="image-gen-content-area" ref={messagesContainerRef}>
                    <div className="generation-slots-hero-grid">
                      {generationSlots.map((slot) => {
                        const generated = generatedImages[slot.id];
                        const isGeneratingSlot = generatingSlots.has(slot.id);
                        const isActive = isGeneratingSlot || generated;
                        const modelName =
                          availableModels.find((m) => m.id === slot.model)?.name || "Model";

                        return (
                          <div
                            key={slot.id}
                            className={`generation-slot-hero-card ${isActive ? 'active' : 'idle'}`}
                            style={{ "--slot-image-ratio": getSlotAspectRatio(slot, generated) }}
                          >
                            <div className="slot-card-top-controls">
                              <div className="slot-card-top-left">
                                <label
                                  className="slot-select-checkbox"
                                  title={
                                    slotSelectionState[slot.id] !== false
                                      ? "This card will receive the next prompt"
                                      : "This card is paused for the next prompt"
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={slotSelectionState[slot.id] !== false}
                                    onChange={() => toggleSlotSelection(slot.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={isGenerating}
                                  />
                                </label>
                                <span className="slot-card-title" title={modelName}>
                                  {modelName}
                                </span>
                              </div>

                              {/* Remove button - only show when idle and multiple slots */}
                              {!isActive && generationSlots.length > 1 ? (
                                <button
                                  className="remove-slot-button-hero"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeGenerationSlot(slot.id);
                                  }}
                                  disabled={isGenerating}
                                >
                                  ×
                                </button>
                              ) : (
                                <div className="slot-top-controls-spacer" aria-hidden="true" />
                              )}
                            </div>

                            {/* Show configuration when idle */}
                            {!isActive && (
                              <div className="slot-hero-config">
                                {/* Model Selector */}
                                <div className="slot-hero-model-section">
                                  <label className="slot-hero-label">Model</label>
                                  <div className="slot-model-wrapper">
                                    <button
                                      className="slot-hero-button"
                                      onClick={() => setActiveSlotDropdown(
                                        activeSlotDropdown === slot.id ? null : slot.id
                                      )}
                                      disabled={loadingModels || isGenerating}
                                    >
                                      <span className="slot-model-name">
                                        {availableModels.find(m => m.id === slot.model)?.name || 'Select Model'}
                                      </span>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="6 9 12 15 18 9" />
                                      </svg>
                                    </button>
                                    {activeSlotDropdown === slot.id && (
                                      <div className="slot-model-dropdown">
                                        {availableModels.map((model) => (
                                          <button
                                            key={model.id}
                                            className={`slot-model-option ${slot.model === model.id ? 'active' : ''}`}
                                            onClick={() => updateSlotModel(slot.id, model.id)}
                                          >
                                            <div className="slot-model-option-name">{model.name}</div>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Resolution/Parameters Section */}
                                <div className="slot-hero-params-section">
                                  {isOpenAiImageModel(slot.model) ? (
                                    <>
                                      {/* Size Selector */}
                                      <div className="slot-hero-param-group">
                                        <label className="slot-hero-label">Size</label>
                                        <button
                                          className="slot-hero-button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveResolutionDropdown(
                                              activeResolutionDropdown === slot.id ? null : slot.id
                                            );
                                          }}
                                          disabled={isGenerating}
                                        >
                                          <span>{slot.size}</span>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="6 9 12 15 18 9" />
                                          </svg>
                                        </button>
                                        {activeResolutionDropdown === slot.id && (
                                          <div className="slot-resolution-dropdown">
                                            {getOpenAiSizeOptions(slot.model).map((opt) => (
                                              <button
                                                key={opt.value}
                                                className={`slot-resolution-option ${slot.size === opt.value ? 'active' : ''}`}
                                                onClick={() => updateSlotSize(slot.id, opt.value)}
                                              >
                                                {opt.label}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {/* Quality Selector */}
                                      <div className="slot-hero-param-group">
                                        <label className="slot-hero-label">Quality</label>
                                        <div className="slot-quality-wrapper">
                                          <button
                                            className="slot-hero-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveQualityDropdown(
                                                activeQualityDropdown === slot.id ? null : slot.id
                                              );
                                            }}
                                            disabled={isGenerating}
                                          >
                                            <span>{slot.quality}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                          </button>
                                          {activeQualityDropdown === slot.id && (
                                            <div className="slot-gemini-dropdown">
                                              {['low', 'medium', 'high', 'auto'].map((qual) => (
                                                <button
                                                  key={qual}
                                                  className={`slot-gemini-option ${slot.quality === qual ? 'active' : ''}`}
                                                  onClick={() => updateSlotQuality(slot.id, qual)}
                                                >
                                                  {qual.charAt(0).toUpperCase() + qual.slice(1)}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </>
                                  ) : isSeeDreamModel(slot.model) ? (
                                    <>
                                      {/* SeeDream Size Selector */}
                                      <div className="slot-hero-param-group">
                                        <label className="slot-hero-label">Size</label>
                                        <button
                                          className="slot-hero-button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveResolutionDropdown(
                                              activeResolutionDropdown === slot.id ? null : slot.id
                                            );
                                          }}
                                          disabled={isGenerating}
                                        >
                                          <span>{slot.size}</span>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="6 9 12 15 18 9" />
                                          </svg>
                                        </button>
                                        {activeResolutionDropdown === slot.id && (
                                          <div className="slot-resolution-dropdown">
                                            {SEEDREAM_SIZE_OPTIONS.map((size) => (
                                              <button
                                                key={size}
                                                className={`slot-resolution-option ${slot.size === size ? 'active' : ''}`}
                                                onClick={() => updateSlotSize(slot.id, size)}
                                              >
                                                {size}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  ) : isFluxModel(slot.model) ? (
                                    <>
                                      {/* FLUX Size/Aspect Selector */}
                                      {isFluxSizeModel(slot.model) ? (
                                        <div className="slot-hero-param-group">
                                          <label className="slot-hero-label">Size</label>
                                          <button
                                            className="slot-hero-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveResolutionDropdown(
                                                activeResolutionDropdown === slot.id ? null : slot.id
                                              );
                                            }}
                                            disabled={isGenerating}
                                          >
                                            <span>{slot.size}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                          </button>
                                          {activeResolutionDropdown === slot.id && (
                                            <div className="slot-resolution-dropdown">
                                              {FLUX_SIZE_OPTIONS.map((size) => (
                                                <button
                                                  key={size}
                                                  className={`slot-resolution-option ${slot.size === size ? 'active' : ''}`}
                                                  onClick={() => updateSlotSize(slot.id, size)}
                                                >
                                                  {size}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="slot-hero-param-group">
                                          <label className="slot-hero-label">Aspect Ratio</label>
                                          <div className="slot-aspect-ratio-wrapper">
                                            <button
                                              className="slot-hero-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveAspectRatioDropdown(
                                                  activeAspectRatioDropdown === slot.id ? null : slot.id
                                                );
                                              }}
                                              disabled={isGenerating}
                                            >
                                              <span>{slot.aspectRatio}</span>
                                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="6 9 12 15 18 9" />
                                              </svg>
                                            </button>
                                            {activeAspectRatioDropdown === slot.id && (
                                              <div className="slot-gemini-dropdown">
                                                {FLUX_ASPECT_RATIO_OPTIONS.map((ratio) => (
                                                  <button
                                                    key={ratio}
                                                    className={`slot-gemini-option ${slot.aspectRatio === ratio ? 'active' : ''}`}
                                                    onClick={() => updateSlotAspectRatio(slot.id, ratio)}
                                                  >
                                                    {ratio}
                                                  </button>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* FLUX output format */}
                                      <div className="slot-hero-param-group">
                                        <label className="slot-hero-label">Format</label>
                                        <div className="slot-output-format-wrapper">
                                          <button
                                            className="slot-hero-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveOutputFormatDropdown(
                                                activeOutputFormatDropdown === slot.id ? null : slot.id
                                              );
                                            }}
                                            disabled={isGenerating}
                                          >
                                            <span>{slot.outputFormat || "jpeg"}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                          </button>
                                          {activeOutputFormatDropdown === slot.id && (
                                            <div className="slot-gemini-dropdown">
                                              {FLUX_OUTPUT_FORMAT_OPTIONS.map((format) => (
                                                <button
                                                  key={format}
                                                  className={`slot-gemini-option ${(slot.outputFormat || "jpeg") === format ? 'active' : ''}`}
                                                  onClick={() => updateSlotOutputFormat(slot.id, format)}
                                                >
                                                  {format}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </>
                                  ) : isGrokModel(slot.model) ? (
                                    <>
                                      {/* Grok Aspect Ratio Selector */}
                                      <div className="slot-hero-param-group">
                                        <label className="slot-hero-label">Aspect Ratio</label>
                                        <div className="slot-aspect-ratio-wrapper">
                                          <button
                                            className="slot-hero-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveAspectRatioDropdown(
                                                activeAspectRatioDropdown === slot.id ? null : slot.id
                                              );
                                            }}
                                            disabled={isGenerating}
                                          >
                                            <span>{slot.aspectRatio}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                          </button>
                                          {activeAspectRatioDropdown === slot.id && (
                                            <div className="slot-gemini-dropdown">
                                              {GROK_ASPECT_RATIO_OPTIONS.map((ratio) => (
                                                <button
                                                  key={ratio}
                                                  className={`slot-gemini-option ${slot.aspectRatio === ratio ? 'active' : ''}`}
                                                  onClick={() => updateSlotAspectRatio(slot.id, ratio)}
                                                >
                                                  {ratio}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Grok Resolution Selector */}
                                      <div className="slot-hero-param-group">
                                        <label className="slot-hero-label">Resolution</label>
                                        <div className="slot-resolution-value-wrapper">
                                          <button
                                            className="slot-hero-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveResolutionValueDropdown(
                                                activeResolutionValueDropdown === slot.id ? null : slot.id
                                              );
                                            }}
                                            disabled={isGenerating}
                                          >
                                            <span>{slot.resolution}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                          </button>
                                          {activeResolutionValueDropdown === slot.id && (
                                            <div className="slot-gemini-dropdown">
                                              {GROK_RESOLUTION_OPTIONS.map((res) => (
                                                <button
                                                  key={res}
                                                  className={`slot-gemini-option ${slot.resolution === res ? 'active' : ''}`}
                                                  onClick={() => updateSlotResolution(slot.id, res)}
                                                >
                                                  {res}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                    </>
                                  ) : (
                                    <>
                                      {/* Aspect Ratio Selector */}
                                      <div className="slot-hero-param-group">
                                        <label className="slot-hero-label">Aspect Ratio</label>
                                        <div className="slot-aspect-ratio-wrapper">
                                          <button
                                            className="slot-hero-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveAspectRatioDropdown(
                                                activeAspectRatioDropdown === slot.id ? null : slot.id
                                              );
                                            }}
                                            disabled={isGenerating}
                                          >
                                            <span>{slot.aspectRatio}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                          </button>
                                          {activeAspectRatioDropdown === slot.id && (
                                            <div className="slot-gemini-dropdown">
                                              {['1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9', '1:4', '4:1', '1:8', '8:1'].map((ratio) => (
                                                <button
                                                  key={ratio}
                                                  className={`slot-gemini-option ${slot.aspectRatio === ratio ? 'active' : ''}`}
                                                  onClick={() => updateSlotAspectRatio(slot.id, ratio)}
                                                >
                                                  {ratio}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Resolution Selector */}
                                      <div className="slot-hero-param-group">
                                        <label className="slot-hero-label">Resolution</label>
                                        <div className="slot-resolution-value-wrapper">
                                          <button
                                            className="slot-hero-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveResolutionValueDropdown(
                                                activeResolutionValueDropdown === slot.id ? null : slot.id
                                              );
                                            }}
                                            disabled={isGenerating}
                                          >
                                            <span>{slot.resolution}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                          </button>
                                          {activeResolutionValueDropdown === slot.id && (
                                            <div className="slot-gemini-dropdown">
                                              {['1K', '2K', '4K'].map((res) => (
                                                <button
                                                  key={res}
                                                  className={`slot-gemini-option ${slot.resolution === res ? 'active' : ''}`}
                                                  onClick={() => updateSlotResolution(slot.id, res)}
                                                >
                                                  {res}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>

                                {/* Empty placeholder icon */}
                                <div className="slot-hero-placeholder">
                                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                    <path d="M21 15l-5-5L5 21" />
                                  </svg>
                                </div>
                              </div>
                            )}

                            {/* Show generating state */}
                            {isGeneratingSlot && (
                              <div className="slot-hero-generating">
                                <div className="slot-hero-header">
                                  <span className="slot-hero-params">
                                    {getSlotParamsLabel(slot)}
                                  </span>
                                </div>
                                <div className="slot-hero-content">
                                  <div className="generating-spinner-hero"></div>
                                  <span>Generating...</span>
                                </div>
                              </div>
                            )}

                            {/* Show generated image */}
                            {generated && (
                              <div className="slot-hero-generated">
                                <div className="slot-hero-header">
                                  <span className="slot-hero-params">
                                    {getSlotParamsLabel(slot)}
                                  </span>
                                </div>
                                <div className="slot-hero-content">
                                  <img
                                    key={generated.imageUrl}
                                    src={generated.isBase64 ? generated.imageUrl : `${config.api.baseUrl}${generated.imageUrl}`}
                                    alt="Generated"
                                    className="slot-hero-image"
                                    onLoad={(e) => handleGeneratedImageLoad(slot.id, e)}
                                    onClick={() => openLightbox(generated, slot)}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Add new slot button */}
                      {generationSlots.length < 8 && (
                        <button
                          className="add-slot-button-hero"
                          onClick={addGenerationSlot}
                          disabled={isGenerating}
                        >
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                          <span>Add Slot</span>
                        </button>
                      )}
                    </div>
                  </div>

              {/* Input Area - Constrained width at bottom */}
              <div className="image-gen-input-section">
                {/* Attached Files Preview */}
                {attachmentPreviews.length > 0 && (
                  <div className="attached-files-container">
                    {attachmentPreviews.map((preview, index) => (
                      <div key={index} className="file-card">
                        <img
                          src={preview.url}
                          alt={preview.file.name}
                          className="file-preview-image"
                        />
                        <div className="file-details">
                          <span className="file-name" title={preview.file.name}>
                            {preview.file.name}
                          </span>
                          <span className="file-size">
                            {formatFileSize(preview.file.size)}
                          </span>
                        </div>
                        <button
                          className="remove-file-button"
                          onClick={() => handleRemoveFile(index)}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="chat-input-wrapper">
                  {chatError && (
                    <div className="chat-error-overlay">{chatError}</div>
                  )}
                  <div className="chat-input-container">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                    <div className="tiptap-wrapper">
                      <EditorContent editor={editor} />
                    </div>
                    <div className="buttons-container">
                      <button
                        className="circular-button plus-button"
                        disabled={!isConnected || isGenerating}
                        onClick={() => fileInputRef.current.click()}
                      >
                        <img src={plusIcon} alt="Add" className="plus-icon" />
                      </button>

                      <button
                        className="circular-button send-button"
                        disabled={
                          !isConnected ||
                          isGenerating ||
                          !hasSelectedSlots ||
                          (!editorHasContent && attachedFiles.length === 0)
                        }
                        onClick={handleSendFromEditor}
                      >
                        <img src={arrow} alt="Send" className="send-icon" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="lightbox-header">
              <div className="lightbox-info">
                <span className="lightbox-model">{lightboxImage.modelName}</span>
                <span className="lightbox-params">{lightboxImage.params}</span>
              </div>
              <button
                className="lightbox-download"
                onClick={() => downloadImage(lightboxImage, `generated-${Date.now()}.png`)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download
              </button>
            </div>
            <div className="lightbox-image-wrapper">
              <img
                src={lightboxImage.isBase64 ? lightboxImage.imageUrl : `${config.api.baseUrl}${lightboxImage.imageUrl}`}
                alt="Full size"
                className="lightbox-image"
              />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ImageGen;
